import React, { useEffect, useState } from 'react';

const RichMessage = ({ text }) => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return (
        <>
            {parts.map((part, i) =>
                part.startsWith('**') && part.endsWith('**')
                    ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
                    : part
            )}
        </>
    );
};

const CONFIGS = {
    success: { bar: 'bg-emerald-500', path: 'M5 13l4 4L19 7' },
    error:   { bar: 'bg-red-500',     path: 'M6 18L18 6M6 6l12 12' },
    warning: { bar: 'bg-amber-500',   path: 'M12 9v2m0 4h.01' },
    info:    { bar: 'bg-blue-500',    path: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
};

const ToastItem = ({ toast, onDismiss, style }) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const frame = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    const cfg = CONFIGS[toast.type] ?? CONFIGS.info;

    return (
        <div
            style={style}
            className={`relative bg-white rounded-xl shadow-lg border border-gray-100 pl-4 pr-3 py-3.5 flex items-start gap-3 overflow-hidden
                ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
            // Note: transition for entrance is handled separately from stack transition
        >
            <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${cfg.bar}`} />
            <div className={`w-7 h-7 rounded-lg ${cfg.bar} text-white flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={cfg.path} />
                </svg>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 leading-tight">{toast.title}</p>
                {toast.message && (
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2"><RichMessage text={toast.message} /></p>
                )}
            </div>
            <button
                onClick={onDismiss}
                className="text-gray-300 hover:text-gray-500 transition flex-shrink-0 mt-0.5 p-0.5 rounded hover:bg-gray-100"
            >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
};

// Approximate height of a single toast (px). Used for stack geometry.
const TOAST_H = 76;
// How many px each older card peeks out from behind the newest when collapsed.
const PEEK = 10;
// Gap between cards in expanded mode.
const GAP = 8;
// Max toasts shown at once.
const MAX = 3;

const ToastContainer = ({ toasts, onDismiss }) => {
    const [expanded, setExpanded] = useState(false);

    if (!toasts || toasts.length === 0) return null;

    const visible = toasts.slice(-MAX);
    const n = visible.length;
    const stacked = n > 1;

    // Container height transitions smoothly between collapsed/expanded
    const collapsedH = TOAST_H + (n - 1) * PEEK;
    const expandedH  = n * TOAST_H + (n - 1) * GAP;
    const containerH = (!stacked || expanded) ? expandedH : collapsedH;

    return (
        <div
            style={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                width: 320,
                height: containerH,
                zIndex: 300,
                transition: 'height 0.35s cubic-bezier(0.4,0,0.2,1)',
            }}
            onMouseEnter={() => stacked && setExpanded(true)}
            onMouseLeave={() => setExpanded(false)}
        >
            {visible.map((toast, i) => {
                // rev=0 → newest (front), rev=n-1 → oldest (back)
                const rev = n - 1 - i;
                const isOpen = !stacked || expanded;

                const bottom   = isOpen ? rev * (TOAST_H + GAP) : rev * PEEK;
                const scale    = isOpen ? 1 : 1 - rev * 0.04;
                const opacity  = isOpen ? 1 : Math.max(0.45, 1 - rev * 0.25);

                return (
                    <ToastItem
                        key={toast.id}
                        toast={toast}
                        onDismiss={() => onDismiss(toast.id)}
                        style={{
                            position: 'absolute',
                            bottom,
                            left: 0,
                            right: 0,
                            transform: `scale(${scale})`,
                            transformOrigin: 'bottom center',
                            opacity,
                            zIndex: n - rev,
                            transition: 'bottom 0.35s cubic-bezier(0.4,0,0.2,1), transform 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.35s ease',
                        }}
                    />
                );
            })}
        </div>
    );
};

export default ToastContainer;
